import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Shield, Menu, ChevronDown, ChevronRight, Sun, Moon, Heart, Phone, Info, Users, BookOpen, Newspaper, Briefcase, Calendar, Mail, LayoutDashboard } from "lucide-react";
import { SearchCommandPalette } from "@/components/SearchCommandPalette";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";


const aboutLinks = [
  { to: "/about/council", label: "About the Council" },
  { to: "/about/foundation", label: "About the Foundation" },
  { to: "/about/pac", label: "About PAC" },
  { to: "/about/team", label: "Council Team" },
  { to: "/about/board", label: "Board of Directors" },
];

const membershipLinks = [
  { to: "/membership", label: "Membership Benefits" },
  { to: "/membership/committees", label: "Committees" },
  { to: "/membership/accomplishments", label: "Accomplishments" },
  { to: "/membership/members", label: "Member List" },
];

const foundationLinks = [
  { to: "/foundation", label: "About the Foundation" },
  { to: "/foundation/nmc", label: "NMC Program" },
];

const navLinks = [
  { to: "/resources", label: "Resources", icon: BookOpen },
  { to: "/news", label: "News", icon: Newspaper },
  { to: "/careers", label: "Careers", icon: Briefcase },
  { to: "/events", label: "Events", icon: Calendar },
  { to: "/contact", label: "Contact Us", icon: Mail },
];

const authNavLinks = [
  { to: "/dashboard", label: "Member Dashboard", icon: LayoutDashboard },
];

const dropdownLinkClass =
  "block select-none rounded-md p-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground";

function DesktopNav({ isAuthenticated }: { isAuthenticated: boolean }) {
  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent">About Us</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[220px] gap-1 p-2">
              {aboutLinks.map(({ to, label }) => (
                <li key={to}>
                  <NavigationMenuLink asChild>
                    <Link to={to} className={dropdownLinkClass}>{label}</Link>
                  </NavigationMenuLink>
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent">Membership</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[220px] gap-1 p-2">
              {membershipLinks.map(({ to, label }) => (
                <li key={to}>
                  <NavigationMenuLink asChild>
                    <Link to={to} className={dropdownLinkClass}>{label}</Link>
                  </NavigationMenuLink>
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent">Foundation</NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="grid w-[220px] gap-1 p-2">
              {foundationLinks.map(({ to, label }) => (
                <li key={to}>
                  <NavigationMenuLink asChild>
                    <Link to={to} className={dropdownLinkClass}>{label}</Link>
                  </NavigationMenuLink>
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>

        {navLinks.map(({ to, label }) => (
          <NavigationMenuItem key={to}>
            <NavigationMenuLink asChild>
              <Link
                to={to}
                className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
              >
                {label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}
        {isAuthenticated && authNavLinks.map(({ to, label }) => (
          <NavigationMenuItem key={to}>
            <NavigationMenuLink asChild>
              <Link
                to={to}
                className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
              >
                {label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function MobileNavSection({ title, icon: Icon, links, onClose }: { title: string; icon: React.ElementType; links: { to: string; label: string }[]; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
        <Icon className="h-4 w-4 text-primary" />
        <span className="flex-1 text-left">{title}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="ml-5 border-l-2 border-primary/20 pl-4">
        {links.map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            onClick={onClose}
            className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <ChevronRight className="h-3 w-3" />
            {label}
          </Link>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function MobileNav({ onClose, isAuthenticated }: { onClose: () => void; isAuthenticated: boolean }) {
  return (
    <div className="flex h-full flex-col">
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto pt-4">
        <MobileNavSection title="About Us" icon={Info} links={aboutLinks} onClose={onClose} />
        <MobileNavSection title="Membership" icon={Users} links={membershipLinks} onClose={onClose} />
        <MobileNavSection title="Foundation" icon={Heart} links={foundationLinks} onClose={onClose} />

        <Separator className="my-2" />

        {navLinks.map(({ to, label, icon: LinkIcon }) => (
          <Link
            key={to}
            to={to}
            onClick={onClose}
            className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <LinkIcon className="h-4 w-4 text-primary" />
            {label}
          </Link>
        ))}

        {isAuthenticated && (
          <>
            <Separator className="my-2" />
            {authNavLinks.map(({ to, label, icon: LinkIcon }) => (
              <Link key={to} to={to} onClick={onClose} className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-primary transition-colors hover:bg-accent hover:text-accent-foreground">
                <LinkIcon className="h-4 w-4" />
                {label}
              </Link>
            ))}
          </>
        )}
      </nav>

      <div className="border-t pt-4 pb-2 space-y-3">
        <Button asChild variant="default" className="w-full gap-2">
          <Link to="/about/pac" onClick={onClose}>
            <Heart className="h-4 w-4" /> Donate to PAC
          </Link>
        </Button>
        <a href="tel:6022529363" className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Phone className="h-3.5 w-3.5" /> (602) 252-9363
        </a>
      </div>
    </div>
  );
}

function AdminMenu({ navigate }: { navigate: (path: string) => void }) {
  return (
    <>
      <DropdownMenuItem onClick={() => navigate("/admin")}>
        <Shield className="mr-2 h-4 w-4" /> Dashboard
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/resources")}>
        <Shield className="mr-2 h-4 w-4" /> Manage Resources
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/contacts")}>
        <Shield className="mr-2 h-4 w-4" /> Contact Submissions
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/careers")}>
        <Shield className="mr-2 h-4 w-4" /> Manage Jobs
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/newsletter")}>
        <Shield className="mr-2 h-4 w-4" /> Newsletter Subscribers
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/events")}>
        <Shield className="mr-2 h-4 w-4" /> Manage Events
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/team")}>
        <Shield className="mr-2 h-4 w-4" /> Manage Team
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/board")}>
        <Shield className="mr-2 h-4 w-4" /> Manage Board
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/members")}>
        <Shield className="mr-2 h-4 w-4" /> Manage Members
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/announcements")}>
        <Shield className="mr-2 h-4 w-4" /> Announcements
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/documents")}>
        <Shield className="mr-2 h-4 w-4" /> Documents
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/testimonials")}>
        <Shield className="mr-2 h-4 w-4" /> Testimonials
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/news")}>
        <Shield className="mr-2 h-4 w-4" /> News & Updates
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/groups")}>
        <Shield className="mr-2 h-4 w-4" /> Member Groups
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/communications")}>
        <Shield className="mr-2 h-4 w-4" /> Communications
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate("/admin/users")}>
        <Shield className="mr-2 h-4 w-4" /> User Management
      </DropdownMenuItem>
    </>
  );
}

export function Navbar() {
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const ThemeButton = () => (
    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleTheme}>
      <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  );

  const UserMenu = ({ iconOnly = false }: { iconOnly?: boolean }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {iconOnly ? (
          <Button variant="outline" size="icon" className="h-9 w-9">
            <User className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="outline" size="sm">
            <User className="mr-1 h-4 w-4" />
            {user?.email?.split("@")[0]}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isAdmin && <AdminMenu navigate={navigate} />}
        <DropdownMenuItem onClick={() => signOut()}>
          <LogOut className="mr-2 h-4 w-4" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      {/* Top utility bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs sm:text-sm">
          <a href="tel:6022529363" className="hidden items-center gap-1.5 hover:underline sm:inline-flex">
            <Phone className="h-3 w-3" /> (602) 252-9363
          </a>
          <div className="flex w-full items-center justify-end gap-4 sm:w-auto">
            <Link
              to="/about/pac"
              className="inline-flex items-center gap-1.5 font-medium transition-opacity hover:opacity-80"
            >
              <Heart className="h-3 w-3" /> Donate to PAC
            </Link>
          </div>
        </div>
      </div>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/achs-logo.svg"
            alt="Arizona Council of Human Service Providers"
            className="h-10 w-auto"
            width="130"
            height="40"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-2">
          <DesktopNav isAuthenticated={!!user} />
          <SearchCommandPalette />
          <ThemeButton />
          {user ? <UserMenu /> : (
            <Button size="sm" asChild><Link to="/login">Sign In</Link></Button>
          )}
        </div>

        {/* Mobile nav */}
        <div className="flex lg:hidden items-center gap-2">
          <SearchCommandPalette />
          <ThemeButton />
          {user ? <UserMenu iconOnly /> : (
            <Button size="sm" asChild><Link to="/login">Sign In</Link></Button>
          )}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="flex flex-col">
              <SheetHeader className="flex-row items-center gap-2 space-y-0">
                <img src="/achs-logo.svg" alt="ACHS" className="h-8 w-auto" width="104" height="32" />
                <SheetTitle className="sr-only">Menu</SheetTitle>
              </SheetHeader>
              <MobileNav onClose={() => setMobileOpen(false)} isAuthenticated={!!user} />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
